/**
 * StaffAndRoles — the single entry point that replaces the split between
 * People, Staff and Settings › Team & roles.
 *
 * Operator decisions this implements are recorded in
 * `docs/staff-and-roles-spec.md`. The two that shape the code most:
 *
 *  • **Roles are per LOCATION, and there is no ranking.** A person holds a
 *    `(location, role)` row for each location they work at. This screen RECORDS
 *    those; it does not compute an "effective role" and must not display one —
 *    no rule exists that would make it true. `can()` keeps resolving exactly as
 *    it does today. That gap is deliberate and documented, not an oversight.
 *  • **Sign-in and bookable are independent.** Either, both, or neither. A
 *    bookable person with no login is a real state and is shown as one.
 *
 * Nothing is retired in this pass: People, Staff and Settings › Team & roles
 * all stay reachable by URL until this screen is signed off.
 */
import { useMemo, useState } from "react";
import { alpha, Avatar, Btn, Card, Checkbox, Icon, Input, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { FilterBar, FilterDropdown, EmptyState, LoadState } from "../../shell/composites";
import { toast } from "../../shell/toast";
import {
  can, PERM_CATALOG, PROCESSES, type ProcessId,
  listRoles, getRoleDef, getRolePermsLive, setRolePerms, resetRole, isRoleCustomised,
  inviteMemberOp, resendInviteOp, setMemberRoleOp, removeMemberOp,
  listApiRoles, getApiRole, type ApiRole,
  effectivePerms, startImpersonation,
} from "../../shell/access";
import { useT, t as i18nT } from "../../lib/i18n";
import { useNav } from "../../shell/router";
import { logAction } from "../../lib/audit";
import { usePeople, type Person } from "./usePeople";
import { useStaff, type StaffMember } from "../staff/useStaff";

type Tab = "people" | "groups" | "roles";

const TH: React.CSSProperties = { padding: "9px 12px", fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: T.soft, whiteSpace: "nowrap", textAlign: "left" };
const TD: React.CSSProperties = { padding: "10px 12px", verticalAlign: "middle" };

/* ── Per-location role rows — the heart of the Add form and the record ───── */

export interface LocRole { locationId: string; roleKey: string; home?: boolean }

/* ── Workspace roles (ported from Settings › Team & roles, retired 2026-08-10) ──
 * TWO role vocabularies meet on this screen, deliberately unmapped:
 *   • WORKSPACE roles — what the team API administers (owner/admin/member…).
 *     Invites and membership writes carry these.
 *   • PROCESS roles — shell/access.ts's capability model `can()` resolves
 *     against, recorded per person and per location and edited on the Roles tab.
 * No equivalence is invented between them. */
function WorkspaceRolePicker({ roles, value, onChange }: { roles: ApiRole[]; value: string; onChange: (key: string) => void }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {roles.map((r) => {
        const on = value === r.key;
        return (
          <button key={r.key} type="button" onClick={() => onChange(r.key)}
            style={{ padding: "7px 14px", borderRadius: 999, fontSize: 13, fontWeight: 600, border: `2px solid ${on ? T.p1 : T.border}`, background: on ? T.p4 : T.white, color: on ? T.p1 : T.n2, cursor: "pointer", fontFamily: T.font }}>
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

/** Capability rows for a WORKSPACE role. Editable only on invite, where the API
 *  accepts a NARROWED set; it rejects capabilities above the role. */
function ApiCapabilityList({ role, selected, onChange }: { role: ApiRole | undefined; selected?: Set<string>; onChange?: (next: Set<string>) => void }) {
  if (!role) return null;
  const readOnly = !onChange;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px" }}>
      {role.capabilities.map((capId, i) => {
        const checked = readOnly ? true : !!selected?.has(capId);
        return (
          <label key={capId} style={{ display: "flex", alignItems: "center", gap: 8, cursor: readOnly ? "default" : "pointer", padding: "2px 0" }}>
            <Checkbox checked={checked} disabled={readOnly} onChange={(on) => {
              if (!onChange) return;
              const next = new Set(selected ?? []);
              if (on) next.add(capId); else next.delete(capId);
              onChange(next);
            }} />
            <span style={{ fontSize: 12.5, color: checked ? T.n1 : T.soft, textDecoration: checked ? "none" : "line-through" }}>{role.permissions[i] ?? capId}</span>
          </label>
        );
      })}
    </div>
  );
}

function LocationRoleRows({ rows, locations, roles, defaultRole, onChange, disabled }: {
  rows: LocRole[];
  locations: { id: string; name: string }[];
  roles: { key: string; label: string }[];
  /** The person's default role. A row with no `roleKey` inherits it — that is
   *  what an empty value MEANS here, not "unset" (operator, 2026-08-08). */
  defaultRole: { key: string; label: string } | null;
  onChange: (r: LocRole[]) => void;
  disabled?: boolean;
}) {
  const t = useT();
  const set = (i: number, patch: Partial<LocRole>) => onChange(rows.map((r, n) => (n === i ? { ...r, ...patch } : r)));
  const sel: React.CSSProperties = { height: 36, borderRadius: 4, border: `1px solid ${T.n4}`, background: T.white, color: T.n1, fontSize: 13.5, fontFamily: T.font, padding: "0 9px", minWidth: 0 };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select disabled={disabled} value={r.locationId} onChange={(e) => set(i, { locationId: e.target.value })} style={{ ...sel, flex: 1.15 }}>
            <option value="">{i18nT("staffRoles.chooseLocation", "Choose a location…")}</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <select disabled={disabled} value={r.roleKey} onChange={(e) => set(i, { roleKey: e.target.value })}
            title={r.roleKey ? undefined : (defaultRole ? i18nT("staffRoles.sameAsDefault", "Same as default") + " — " + defaultRole.label : undefined)}
            style={{ ...sel, flex: 1.45, color: r.roleKey ? T.n1 : T.n2, fontStyle: r.roleKey ? "normal" : "italic" }}>
            {/* Just "Same as default" — NOT the role name. The default is named
                in full in its own picker directly above this list, and a
                collapsed <select> here is ~150px: spelling it out per row read
                "Same as default (Workf…" and clipped the very word it was
                trying to show. The full text is on the title attribute. */}
            <option value="">
              {defaultRole
                ? i18nT("staffRoles.sameAsDefault", "Same as default")
                : i18nT("staffRoles.chooseRole", "Choose a role…")}
            </option>
            {roles.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
          </select>
          <button type="button" disabled={disabled} onClick={() => onChange(rows.map((x, n) => ({ ...x, home: n === i })))}
            title={t("staffRoles.markHome", "Mark as home location")}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, height: 36, padding: "0 10px", borderRadius: 4, border: `1px solid ${r.home ? T.p1 : T.n4}`, background: r.home ? T.p4 : T.white, color: r.home ? T.p1 : T.soft, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.font, whiteSpace: "nowrap" }}>
            <Icon.loc size={12} />{t("staffRoles.home", "Home")}
          </button>
          <button type="button" disabled={disabled} onClick={() => onChange(rows.filter((_, n) => n !== i))}
            title={t("common.actions.remove", "Remove")}
            style={{ border: "none", background: "transparent", color: T.n3, cursor: "pointer", display: "inline-flex", padding: 4 }}>
            <Icon.x size={14} />
          </button>
        </div>
      ))}
      <button type="button" disabled={disabled} onClick={() => onChange([...rows, { locationId: "", roleKey: "", home: rows.length === 0 }])}
        style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 6, height: 32, padding: "0 11px", borderRadius: 6, border: `1px solid ${T.borderStrong}`, background: T.white, color: T.p1, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>
        <Icon.plus size={12} /> {t("staffRoles.addLocation", "Add a location")}
      </button>
      <div style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.45 }}>
        {t("staffRoles.perLocationNote", "Every location uses the default role unless you set a different one on that row. Roles are not ranked — holding two doesn't combine them.")}
      </div>
    </div>
  );
}

/* ── Add staff — one form, everything at once ────────────────────────────── */

function AddStaffForm({ locations, roles, lobs, onClose, onDone }: {
  locations: { id: string; name: string }[];
  roles: { key: string; label: string }[];
  lobs: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const { upsertMember } = useStaff();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [defaultRoleKey, setDefaultRoleKey] = useState("");
  const [rows, setRows] = useState<LocRole[]>([{ locationId: "", roleKey: "", home: true }]);
  const [lob, setLob] = useState<string[]>([]);
  const [signsIn, setSignsIn] = useState(true);
  const [bookable, setBookable] = useState(true);
  /* Workspace role + optional capability narrowing (ported from the retired
     Settings › Team & roles invite modal). Only meaningful when the person
     signs in — a bookable-only record has no membership to carry it. */
  const apiRoles = listApiRoles();
  const [wsRoleKey, setWsRoleKey] = useState("");
  const wsRole = getApiRole(wsRoleKey || apiRoles[0]?.key || "");
  const [narrowed, setNarrowed] = useState(false);
  const [caps, setCaps] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = i18nT("staffRoles.nameRequired", "Enter a name.");
    if (signsIn && !/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email.trim())) e.email = i18nT("staffRoles.emailRequired", "A valid email is required for anyone who can sign in.");
    if (!defaultRoleKey) e.role = i18nT("staffRoles.defaultRoleRequired", "Choose the role they hold by default.");
    if (!rows.some((r) => r.locationId)) e.rows = i18nT("staffRoles.locationRequired", "Assign at least one location.");
    return e;
  };

  const save = async () => {
    const e = validate();
    setErr(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    const filled = rows.filter((r) => r.locationId);
    const parts = name.trim().split(/\s+/);
    const now = new Date().toISOString();
    try {
      /* The ROSTER RECORD is the person, and it is created for real. This form
         used to toast "Staff member added" for a non-signing-in person without
         calling a single op (no-fake-interactivity). Locations and the role at
         each ride in the create payload, so the record lands complete instead
         of needing a second write. */
      await upsertMember({
        id: "", staffId: "",
        firstName: parts[0] ?? "", lastName: parts.slice(1).join(" "),
        name: name.trim(), email: email.trim(), phone: "", title: title.trim(),
        bio: "", photo: "", photoUrl: null, color: null,
        status: "active", signsIn, role: defaultRoleKey,
        channels: bookable ? ["appointments"] : [],
        groups: [], serviceGrants: [], serviceExclusions: [],
        skills: [], languages: [], department: null, category: null, tags: [],
        locations: filled.map((r) => ({ locationId: r.locationId, roleKey: r.roleKey || null, home: !!r.home })),
        concurrency: 1, dailyCap: 0, timeOff: [],
        slug: "", linkEnabled: false, createdAt: now, updatedAt: now,
      } as StaffMember);
      /* Sign-in is a separate, MEMBERSHIP fact: only invite when the switch is
         on. The role it carries is the person's DEFAULT — the membership holds
         one role, and the default is precisely the role that isn't tied to a
         single location. */
      if (signsIn) {
        await inviteMemberOp({
          displayName: name.trim(), email: email.trim(),
          roleKey: wsRole?.key ?? defaultRoleKey,
          capabilities: narrowed ? [...caps] : undefined,
        });
        logAction("staff", email.trim(), name.trim(), "invited");
      } else {
        logAction("staff", name.trim(), name.trim(), "created");
      }
      toast({
        tone: "success",
        title: signsIn ? t("staffRoles.invited", "Invitation sent") : t("staffRoles.staffAdded", "Staff member added"),
        subtitle: name.trim(),
      });
      onDone();
      onClose();
    } catch (x) {
      toast({ tone: "danger", title: t("staffRoles.couldnTAdd", "Couldn't add this person"), subtitle: x instanceof Error ? x.message : String(x) });
    } finally {
      setSaving(false);
    }
  };

  const label = (s: string, req?: boolean) => (
    <div style={{ fontSize: 12.5, fontWeight: 600, color: T.n2, marginBottom: 5 }}>{s}{req ? <span style={{ color: T.red1 }}> *</span> : null}</div>
  );

  return (
    <Card style={{ padding: 20, marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.n1 }}>{t("staffRoles.addAStaffMember", "Add a staff member")}</h3>
        <span style={{ flex: 1 }} />
        <button onClick={onClose} style={{ border: "none", background: "transparent", color: T.soft, cursor: "pointer", display: "inline-flex" }}><Icon.x size={16} /></button>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200 }}>{label(t("staffRoles.fullName", "Full name"), true)}
          <Input value={name} onChange={(e) => setName(e.target.value)} error={err.name} placeholder="Marcus Thompson" /></div>
        <div style={{ flex: 1, minWidth: 200 }}>{label(t("staffRoles.email", "Email"), signsIn)}
          <Input value={email} onChange={(e) => setEmail(e.target.value)} error={err.email} placeholder="m.thompson@example.com" /></div>
        <div style={{ flex: 1, minWidth: 180 }}>{label(t("staffRoles.jobTitle", "Job title"))}
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Personal banker" /></div>
      </div>

      <div style={{ marginBottom: 14, maxWidth: 420 }}>
        {label(t("staffRoles.defaultRole", "Default role"), true)}
        <select value={defaultRoleKey} onChange={(e) => setDefaultRoleKey(e.target.value)}
          style={{ width: "100%", height: 38, borderRadius: 4, border: `1px solid ${err.role ? T.red1 : T.n4}`, background: T.white, color: T.n1, fontSize: 13.5, fontFamily: T.font, padding: "0 10px" }}>
          <option value="">{i18nT("staffRoles.chooseRole", "Choose a role…")}</option>
          {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <div style={{ marginTop: 5, fontSize: 11.5, color: err.role ? T.red1 : T.soft, lineHeight: 1.45 }}>
          {err.role || t("staffRoles.defaultRoleNote", "Applied at every location they work at. A location can be given a different role below.")}
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        {label(t("staffRoles.locationsAndRoles", "Locations & role at each"), true)}
        <LocationRoleRows rows={rows} locations={locations} roles={roles} onChange={setRows}
          defaultRole={defaultRoleKey ? { key: defaultRoleKey, label: roles.find((r) => r.key === defaultRoleKey)?.label ?? defaultRoleKey } : null} />
        {err.rows ? <div style={{ marginTop: 6, fontSize: 12, color: T.red1 }}>{err.rows}</div> : null}
      </div>

      <div style={{ marginBottom: 14 }}>
        {label(t("staffRoles.linesOfBusiness", "Lines of business"))}
        {!lobs.length ? (
          <div style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "10px 12px", borderRadius: 9, background: T.canvas, border: `1px dashed ${T.n4}` }}>
            <span style={{ color: T.soft, flexShrink: 0, marginTop: 1 }}><Icon.info size={14} /></span>
            <span style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.45 }}>
              {t("staffRoles.lobsNotServed", "Lines of business aren't served to this screen yet — they live in the tenant's services catalogue. Assigning one needs listServiceLobs; declared in docs/staff-and-roles-spec.md.")}
            </span>
          </div>
        ) : null}
      </div>
      {lobs.length ? (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {lobs.map((l) => {
              const on = lob.includes(l);
              return (
                <button key={l} type="button" onClick={() => setLob(on ? lob.filter((x) => x !== l) : [...lob, l])}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 30, padding: "0 12px", borderRadius: 15, border: `1px solid ${on ? T.p1 : T.n4}`, background: on ? T.p4 : T.white, color: on ? T.p1 : T.n2, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>
                  {on ? <Icon.check size={12} /> : null}{l}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", padding: "12px 14px", borderRadius: 9, background: T.canvas, marginBottom: 16 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 9, cursor: "pointer" }}>
          <Checkbox checked={signsIn} onChange={() => setSignsIn(!signsIn)} />
          <span><span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: T.n1 }}>{t("staffRoles.canSignIn", "Can sign in")}</span>
            <span style={{ display: "block", fontSize: 11.5, color: T.soft }}>{t("staffRoles.sendsAnInvite", "Sends an invitation to their email")}</span></span>
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 9, cursor: "pointer" }}>
          <Checkbox checked={bookable} onChange={() => setBookable(!bookable)} />
          <span><span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: T.n1 }}>{t("staffRoles.canBeBooked", "Can be booked")}</span>
            <span style={{ display: "block", fontSize: 11.5, color: T.soft }}>{t("staffRoles.appearsInAvailability", "Appears in availability and the booking flow")}</span></span>
        </label>
      </div>

      {signsIn ? (
        <div style={{ marginBottom: 16 }}>
          {label(t("staffRoles.workspaceRole", "Workspace role"), true)}
          {apiRoles.length ? (
            <>
              <WorkspaceRolePicker roles={apiRoles} value={wsRole?.key ?? ""} onChange={setWsRoleKey} />
              <div style={{ marginTop: 6, fontSize: 11.5, color: T.soft, lineHeight: 1.45 }}>
                {wsRole?.description || t("staffRoles.workspaceRoleNote", "What their sign-in grants at workspace level. Separate from the default role above, which is the process role capability checks resolve against.")}
              </div>
              <button type="button" onClick={() => { const next = !narrowed; setNarrowed(next); if (next && wsRole) setCaps(new Set(wsRole.capabilities)); }}
                style={{ marginTop: 9, background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: T.p1, fontFamily: T.font, display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Icon.chev size={12} dir={narrowed ? "up" : "down"} />
                {narrowed ? t("staffRoles.hideCapabilities", "Hide capabilities") : t("staffRoles.narrowCapabilities", "Narrow the capabilities of this invite")}
              </button>
              {narrowed ? (
                <div style={{ marginTop: 10, padding: "12px 14px", background: T.n6, borderRadius: 9 }}>
                  <ApiCapabilityList role={wsRole} selected={caps} onChange={setCaps} />
                </div>
              ) : null}
            </>
          ) : (
            <div style={{ fontSize: 12.5, color: T.soft, padding: "10px 12px", background: T.n6, borderRadius: 9 }}>
              {t("staffRoles.workspaceRolesNotLoaded", "Workspace roles aren't loaded yet — the invite will carry the default role above.")}
            </div>
          )}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn variant="secondary" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
        <Btn icon={<Icon.check size={14} />} disabled={saving} onClick={() => void save()}>
          {saving ? t("staffRoles.saving", "Saving…") : t("staffRoles.addStaffMember", "Add staff member")}
        </Btn>
      </div>
    </Card>
  );
}

/* ── Roles tab — the process-role capability editor, out of its modal ────── */

function RolesTab({ people }: { people: Person[] }) {
  const t = useT();
  const canWrite = can("team.write") || can("settings.write");
  const [process, setProcess] = useState<ProcessId>(PROCESSES[0]?.id ?? ("events" as ProcessId));
  const roles = listRoles(process);
  const [selected, setSelected] = useState<string>(roles[0]?.id ?? "");
  /* getRoleDef takes (role, process) — the opposite order to its siblings. */
  const def = selected ? getRoleDef(selected, process) : null;
  const saved = useMemo(() => (selected ? getRolePermsLive(process, selected) : []), [process, selected]);
  /* Draft state: edits are staged and committed by Save changes, so an
     accidental click isn't an immediate change for everyone holding the role. */
  const [draft, setDraft] = useState<string[] | null>(null);
  const live = draft ?? saved;
  const dirty = draft !== null && (draft.length !== saved.length || draft.some((c) => !saved.includes(c)));

  const pick = (id: string) => { setSelected(id); setDraft(null); };
  const toggle = (cap: string) => {
    if (!canWrite) return;
    setDraft(live.includes(cap) ? live.filter((c) => c !== cap) : [...live, cap]);
  };
  const save = () => {
    if (!dirty || !selected) return;
    setRolePerms(process, selected, live);
    logAction("settings", process + ":" + selected, def?.label ?? selected, "updated");
    setDraft(null);
    toast({ tone: "success", title: t("staffRoles.roleSaved", "Role saved"), subtitle: def?.label ?? selected });
  };

  /* Holders come from the roster already on screen — a real count, not a guess. */
  const holders = (roleId: string) =>
    people.filter((x) => (x.assignments ?? {})[process] === roleId).length;
  const capCount = (roleId: string) => getRolePermsLive(process, roleId).length;
  const roleHue = (r: { id: string; color?: string }, i: number) =>
    r.color ?? ["#4F00F2", "#C8861E", "#0AA396", "#2B9E5F", "#0E7490", "#8955F2", "#D61A55"][i % 7];

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700, color: T.n1 }}>{t("staffRoles.manageRoles", "Manage roles")}</h3>
        <p style={{ margin: 0, fontSize: 12.5, color: T.soft, lineHeight: 1.5, maxWidth: 720 }}>
          {t("staffRoles.manageRolesBlurb", "Set the default capabilities each role holds. Per-person exceptions live on each member. Preview: role edits persist in this browser only — server-side role persistence is REQ-088.")}
        </p>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        {PROCESSES.map((pr) => {
          const on = process === pr.id;
          return (
            <button key={pr.id} onClick={() => { setProcess(pr.id); const r = listRoles(pr.id); setSelected(r[0]?.id ?? ""); setDraft(null); }}
              style={{ height: 34, padding: "0 15px", borderRadius: 6, border: `1px solid ${on ? T.p1 : T.n4}`, background: on ? T.p4 : T.white, color: on ? T.p1 : T.n2, fontSize: 13.5, fontWeight: on ? 700 : 500, cursor: "pointer", fontFamily: T.font }}>
              {pr.label}
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <Card style={{ width: 232, flexShrink: 0, padding: 8 }}>
          {roles.map((r, i) => {
            const on = r.id === selected;
            const n = holders(r.id);
            return (
              <button key={r.id} onClick={() => pick(r.id)}
                style={{ display: "flex", gap: 10, width: "100%", padding: "10px 10px", borderRadius: 8, border: "none", background: on ? T.p4 : "transparent", cursor: "pointer", textAlign: "left", fontFamily: T.font, alignItems: "flex-start" }}>
                <span style={{ width: 11, height: 11, borderRadius: 3, background: roleHue(r, i), flexShrink: 0, marginTop: 3 }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13.5, fontWeight: on ? 700 : 600, color: on ? T.p1 : T.n1 }}>{r.label}</span>
                  <span style={{ display: "block", fontSize: 11.5, color: n ? T.soft : T.orange1, marginTop: 1 }}>
                    {n ? n : t("staffRoles.noHolders", "No holders")} · {capCount(r.id)} {t("staffRoles.caps", "caps")}
                  </span>
                </span>
              </button>
            );
          })}
          <button type="button" disabled
            title={i18nT("staffRoles.pendingOp", "Not served yet — needs ") + "createRole"}
            style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", marginTop: 4, padding: "10px", borderRadius: 8, border: `1px dashed ${T.n4}`, background: "transparent", color: T.n3, fontSize: 13, fontWeight: 600, cursor: "not-allowed", fontFamily: T.font }}>
            <Icon.plus size={13} /> {t("staffRoles.newRole", "New role")}
          </button>
        </Card>

        <Card style={{ flex: 1, minWidth: 340, padding: 18 }}>
          {!def ? (
            <EmptyState variant="plain" icon={<Icon.lock size={18} />} title={t("staffRoles.pickARole", "Pick a role")} body={t("staffRoles.chooseARoleToSee", "Choose a role to see what it grants.")} />
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                <Pill tone="primary">{def.label}</Pill>
                <span style={{ fontSize: 13, color: T.n2 }}>{live.length} {t("staffRoles.capabilities", "capabilities")}</span>
                <span style={{ flex: 1 }} />
                {canWrite && isRoleCustomised(process, selected) ? (
                  <button onClick={() => { resetRole(process, selected); logAction("settings", process + ":" + selected, def?.label ?? selected, "updated"); setDraft(null); toast({ tone: "info", title: t("staffRoles.roleReset", "Role reset to its default capabilities") }); }}
                    style={{ border: "none", background: "transparent", color: T.p1, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>
                    {t("staffRoles.resetToDefault", "Reset to default")}
                  </button>
                ) : null}
                <Btn size="sm" disabled={!canWrite || !dirty} onClick={save}>{t("staffRoles.saveChanges", "Save changes")}</Btn>
              </div>
              {def.blurb ? (
                <p style={{ margin: "0 0 16px", fontSize: 13, color: T.n2, lineHeight: 1.5 }}>{def.blurb}</p>
              ) : null}
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                {PERM_CATALOG.map((group) => (
                  <div key={group.id}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: T.soft, marginBottom: 9 }}>{group.label}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "9px 18px" }}>
                      {group.caps.map((cap) => (
                        <label key={cap.id} style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 13, color: T.n1, cursor: canWrite ? "pointer" : "default" }}>
                          <Checkbox checked={live.includes(cap.id)} onChange={() => toggle(cap.id)} />
                          <span style={{ minWidth: 0 }}>{cap.label}</span>
                          {cap.risk ? <Pill tone="danger" style={{ fontSize: 9 }}>{t("settings.team.sensitive", "Sensitive")}</Pill> : null}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

/* ── Person record — the edit path for someone who already exists ─────────
 * Opened from a roster row. Real ops run for real; anything with no op behind
 * it is DISABLED and names what it waits on, rather than looking editable and
 * silently discarding the change. */
function PersonRecord({ person, locations, roles, lastLogin, statusPill, onClose, onChanged }: {
  person: Person;
  locations: { id: string; name: string }[];
  roles: { key: string; label: string }[];
  /** Same value the roster's Last login column renders. */
  lastLogin: string;
  /** The roster's own status pill, passed in so the two can never diverge. */
  statusPill: (p: Person) => React.ReactNode;
  onClose: () => void;
  onChanged: () => void;
}) {
  const t = useT();
  const nav = useNav();
  const { setLocationRoles, setDefaultRole } = useStaff();
  const canWrite = can("team.write") || can("team.read");
  /* What this person can ACTUALLY do — role + per-process assignments + any
     per-person grants/revokes, resolved by the same function the shell uses.
     Read-only here: narrowing an individual writes grants/revokes, and no op
     is exported for that yet. */
  const caps = useMemo(() => {
    /* effectivePerms wants a PROCESS role (Event Manager, Personal Banker) —
       person.roleKey is the WORKSPACE role (owner/admin/member) and resolves to
       nothing. SettingsTeam passes `processRole || role`; the equivalent here is
       the person's own per-process assignments, with the events assignment as
       the single-role fallback for rows that predate them. */
    const a = person.assignments ?? {};
    const primary = a.events ?? Object.values(a).find(Boolean) ?? person.roleKey;
    return effectivePerms({ role: primary, assignments: a });
  }, [person]);
  /* The rows ARE the person's assignments — read from the record, written back
     whole (setStaffLocationRoles replaces the set). `stamp` is what Save
     compares against, so the button is live only when something really moved. */
  const rowsOf = (src: Person): LocRole[] =>
    src.locationRoles.length
      ? src.locationRoles.map((r) => ({ locationId: r.locationId, roleKey: r.roleKey, home: r.home }))
      : [{ locationId: "", roleKey: "", home: true }];
  const stamp = (r: LocRole[]) => JSON.stringify(r.filter((x) => x.locationId).map((x) => [x.locationId, x.roleKey, !!x.home]));
  const [rows, setRows] = useState<LocRole[]>(() => rowsOf(person));
  const [savedStamp, setSavedStamp] = useState(() => stamp(rowsOf(person)));
  const [rowsErr, setRowsErr] = useState("");
  const [roleKey, setRoleKey] = useState(person.defaultRoleKey || "");
  /* Workspace role (owner/admin/member…) — the membership vocabulary, ported
     from the retired Settings › Team & roles Edit-access modal. */
  const wsRoles = listApiRoles();
  const [wsKey, setWsKey] = useState(person.roleKey || "");
  const wsDirty = !!wsKey && wsKey !== person.roleKey;
  const [busy, setBusy] = useState<string | null>(null);
  const locsDirty = stamp(rows) !== savedStamp;

  const run = async (id: string, fn: () => Promise<unknown>, ok: string, verb?: string) => {
    if (busy) return;
    setBusy(id);
    try {
      await fn();
      /* Logged only after the op resolves — an entry for a failed write would
         be worse than none. */
      logAction("staff", person.memberId ?? person.key, person.name, verb ?? "updated");
      onChanged();
      toast({ tone: "success", title: ok, subtitle: person.name });
    }
    catch (e) { toast({ tone: "danger", title: t("staffRoles.couldnTSave", "Couldn't save"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(null); }
  };

  const sectionHead = (s: string) => <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: T.soft, margin: "18px 0 8px" }}>{s}</div>;

  /* What the inheriting rows below inherit FROM. It follows the picker, not the
     stored value: the two are saved by different buttons, and rows describing a
     default the admin has already changed would be describing something that is
     no longer on screen. The pending state is announced by a pill on the
     section header — not inside the row's <select>, where it was clipped away
     ("Same as default (Workf…") and never actually read. */
  const labelFor = (k: string) => roles.find((r) => r.key === k)?.label ?? k;
  const effectiveDefault = roleKey || person.defaultRoleKey;
  const rowsDefault = effectiveDefault ? { key: effectiveDefault, label: labelFor(effectiveDefault) } : null;
  const defaultUnsaved = !!roleKey && roleKey !== person.defaultRoleKey;

  /* Per-location roles live on the ROSTER record: that is where locations are
     stored, and it is the only id a bookable person with no workspace
     membership has. Someone with a membership but no roster record therefore
     has nothing to write to — the editor says so instead of pretending. */
  const saveLocations = () => {
    const filled = rows.filter((r) => r.locationId);
    setRowsErr("");
    void run("locs", async () => {
      await setLocationRoles(person.staffId as string, filled.map((r) => ({ locationId: r.locationId, roleKey: r.roleKey, home: !!r.home })));
      setRows(filled.length ? filled : [{ locationId: "", roleKey: "", home: true }]);
      setSavedStamp(stamp(filled));
    }, t("staffRoles.locationsSaved", "Locations & roles saved"), "locations updated");
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(38,34,101,.35)", display: "flex", justifyContent: "flex-end" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 560, maxWidth: "94vw", height: "100%", background: T.white, boxShadow: "-14px 0 40px rgba(16,24,40,.22)", display: "flex", flexDirection: "column" }}>
        <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 12, padding: "16px 20px", borderBottom: `1px solid ${T.border}` }}>
          <Avatar size={40} initials={person.init} color={person.color} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.n1 }}>{person.name}</div>
            <div style={{ fontSize: 12.5, color: T.soft }}>{person.title || person.email || "—"}</div>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "transparent", color: T.soft, cursor: "pointer", display: "inline-flex", padding: 6 }}><Icon.x size={16} /></button>
        </div>

        {/* The roster's columns, repeated verbatim. Opening a person must not
            show them anything different from the row they clicked (operator,
            2026-08-08) — so these read from the SAME values the table renders,
            including its own status pill. */}
        <div style={{ flexShrink: 0, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px", padding: "14px 20px", background: T.canvas, borderBottom: `1px solid ${T.border}` }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: T.soft, marginBottom: 3 }}>{t("staffRoles.roleAccess", "Role / access")}</div>
            <div style={{ fontSize: 13, color: T.n1 }}>{person.roleLabel || roles.find((r) => r.key === person.defaultRoleKey)?.label || person.defaultRoleKey || "—"}</div>
            <div style={{ fontSize: 11.5, color: T.soft, display: "flex", gap: 6, marginTop: 1 }}>
              {person.signsIn ? <span>{t("staffRoles.signsIn", "Signs in")}</span> : null}
              {person.bookable ? <span>{t("staffRoles.bookable", "Bookable")}</span> : null}
              {!person.signsIn && !person.bookable ? <span>{t("staffRoles.recordOnly", "Record only")}</span> : null}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: T.soft, marginBottom: 3 }}>{t("staffRoles.location", "Location")}</div>
            <div style={{ fontSize: 13, color: T.n2 }}>{person.locations.length ? person.locations.map((l) => l.name).join(", ") : "—"}</div>
          </div>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: T.soft, marginBottom: 3 }}>{t("staffRoles.status", "Status")}</div>
            <div>{statusPill(person)}</div>
          </div>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: T.soft, marginBottom: 3 }}>{t("staffRoles.lastLogin", "Last login")}</div>
            <div style={{ fontSize: 13, color: T.soft }}>{lastLogin}</div>
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "4px 20px 24px" }}>
          {sectionHead(t("staffRoles.defaultRole", "Default role"))}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <select value={roleKey} disabled={!canWrite || (!person.staffId && !person.memberId)} onChange={(e) => setRoleKey(e.target.value)}
              style={{ flex: 1, minWidth: 200, height: 38, borderRadius: 4, border: `1px solid ${T.n4}`, background: (person.staffId || person.memberId) ? T.white : T.n6, color: T.n1, fontSize: 13.5, fontFamily: T.font, padding: "0 10px" }}>
              <option value="">{i18nT("staffRoles.chooseRole", "Choose a role…")}</option>
              {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
            <Btn size="sm" disabled={!canWrite || (!person.staffId && !person.memberId) || !roleKey || roleKey === person.defaultRoleKey || busy !== null}
              onClick={() => void run("role", async () => {
                /* The default lives on the ROSTER record — the only record a
                   person without a membership has. The WORKSPACE role is a
                   different vocabulary and is saved by its own section below
                   (the two were conflated before Team & roles merged in here). */
                if (person.staffId) await setDefaultRole(person.staffId, roleKey);
              }, t("staffRoles.roleUpdated", "Default role updated"), "role changed")}>
              {busy === "role" ? t("staffRoles.saving", "Saving…") : t("common.actions.save", "Save")}
            </Btn>
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: T.soft, lineHeight: 1.45 }}>
            {t("staffRoles.defaultRoleNote", "Applied at every location they work at. A location can be given a different role below.")}
          </div>
          {!person.memberId ? (
            <div style={{ marginTop: 6, fontSize: 12, color: T.soft }}>
              {t("staffRoles.noMembership", "This person has no workspace membership — they can be booked but cannot sign in.")}
            </div>
          ) : null}

          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "18px 0 8px" }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: T.soft }}>{t("staffRoles.locationsAndRoles", "Locations & role at each")}</span>
            {defaultUnsaved ? <Pill tone="warn">{t("staffRoles.newDefaultUnsaved", "New default not saved")}</Pill> : null}
            <span style={{ flex: 1 }} />
            <Btn size="sm" disabled={!canWrite || !person.staffId || !locsDirty || busy !== null} onClick={saveLocations}>
              {busy === "locs" ? t("staffRoles.saving", "Saving…") : t("common.actions.save", "Save")}
            </Btn>
          </div>
          <LocationRoleRows rows={rows} locations={locations} roles={roles} onChange={setRows} disabled={!canWrite || !person.staffId}
            defaultRole={rowsDefault} />
          {rowsErr ? <div style={{ marginTop: 6, fontSize: 12, color: T.red1 }}>{rowsErr}</div> : null}
          {!person.staffId ? (
            <div style={{ marginTop: 10, display: "flex", gap: 9, alignItems: "flex-start", padding: "10px 12px", borderRadius: 9, background: T.canvas, border: `1px dashed ${T.n4}` }}>
              <span style={{ color: T.soft, flexShrink: 0, marginTop: 1 }}><Icon.info size={14} /></span>
              <span style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.45 }}>
                {t("staffRoles.noRosterRecord", "Where someone works is stored on their roster record, and this person doesn't have one — they hold a workspace membership only. Add them to the staff roster to record locations and a role at each.")}
              </span>
            </div>
          ) : null}

          {person.memberId ? (
            <>
              {sectionHead(t("staffRoles.workspaceRole", "Workspace role"))}
              {wsRoles.length ? (
                <>
                  <WorkspaceRolePicker roles={wsRoles} value={wsKey} onChange={setWsKey} />
                  <div style={{ marginTop: 6, fontSize: 12, color: T.soft, lineHeight: 1.45 }}>
                    {getApiRole(wsKey)?.description || t("staffRoles.workspaceRoleNote", "What their sign-in grants at workspace level. Separate from the default role above, which is the process role capability checks resolve against.")}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 9 }}>
                    <Btn size="sm" disabled={!canWrite || !wsDirty || busy !== null}
                      onClick={() => void run("wsrole", () => setMemberRoleOp(person.memberId as string, wsKey), t("staffRoles.accessUpdated", "Workspace access updated"), "workspace role changed")}>
                      {busy === "wsrole" ? t("staffRoles.saving", "Saving…") : t("staffRoles.saveAccess", "Save access")}
                    </Btn>
                    <span style={{ fontSize: 11.5, color: T.soft }}>
                      {t("staffRoles.perPersonOverridesPending", "Per-person capability overrides aren't available yet — no declared op (REQ-074).")}
                    </span>
                  </div>
                  {getApiRole(wsKey) ? (
                    <div style={{ marginTop: 12, padding: "10px 12px", background: alpha(T.p1, "0a"), border: `1px solid ${T.p2}`, borderRadius: 9 }}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: T.soft, marginBottom: 7 }}>{t("staffRoles.capabilitiesInThisRole", "Capabilities in this role")}</div>
                      <ApiCapabilityList role={getApiRole(wsKey)} />
                    </div>
                  ) : null}
                </>
              ) : (
                <div style={{ fontSize: 12.5, color: T.soft, padding: "10px 12px", background: T.n6, borderRadius: 9 }}>
                  {t("staffRoles.workspaceRolesNotLoaded2", "Workspace roles aren't loaded yet — reopen once the roster has finished loading.")}
                </div>
              )}
            </>
          ) : null}

          {sectionHead(t("staffRoles.whatTheyCanDo", "What they can do"))}
          {caps.size ? (
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: T.n1 }}>{caps.size}</span>
              <span style={{ fontSize: 12.5, color: T.soft }}>{t("staffRoles.capabilitiesResolved", "capabilities, resolved from their role and assignments")}</span>
            </div>
          ) : (
            /* No process-role assignment on this row, so there is nothing to
               resolve. Saying "0 capabilities" would be a claim about their
               access; this says what is actually true. */
            <div style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "10px 12px", borderRadius: 9, background: T.canvas, border: `1px dashed ${T.n4}` }}>
              <span style={{ color: T.soft, flexShrink: 0, marginTop: 1 }}><Icon.info size={14} /></span>
              <span style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.45 }}>
                {t("staffRoles.noProcessRole", "No capabilities can be resolved for this person: capabilities come from a process role (Event Manager, Personal Banker) and this record carries only a workspace role. Their per-location roles are recorded, but by design they don't feed access resolution yet — see docs/staff-and-roles-spec.md.")}
              </span>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: 240, overflow: "auto", padding: "10px 12px", borderRadius: 9, background: T.canvas }}>
            {PERM_CATALOG.map((group) => {
              const held = group.caps.filter((c) => caps.has(c.id));
              if (!held.length) return null;
              return (
                <div key={group.id}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: T.soft, marginBottom: 4 }}>{group.label}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {held.map((c) => (
                      <span key={c.id} style={{ fontSize: 11.5, color: T.n2, background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: "2px 9px" }}>{c.label}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 7, fontSize: 11.5, color: T.soft, lineHeight: 1.45 }}>
            {t("staffRoles.narrowingNeedsOp", "Narrowing an individual's capabilities writes per-person grants/revokes — no op is exported for that yet. Editing the ROLE, in the Roles tab, changes it for everyone who holds it.")}
          </div>

          {sectionHead(t("staffRoles.actions", "Actions"))}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Btn size="sm" variant="secondary" disabled={busy !== null || (!person.roleKey && !person.defaultRoleKey && !Object.keys(person.assignments ?? {}).length)}
              title={(!person.roleKey && !person.defaultRoleKey && !Object.keys(person.assignments ?? {}).length)
                ? i18nT("staffRoles.cantActAsNoRole", "No role is recorded for this person, so there is nothing to act as yet.")
                : undefined}
              onClick={() => {
                /* Acting-as, not becoming: the banner in the shell names the
                   admin the whole time and the audit trail attributes what
                   they do to THEM, on this person's behalf. Nothing is logged
                   or navigated unless the swap actually took — it used to
                   toast and navigate even when startImpersonation refused. */
                const started = startImpersonation({
                  id: person.memberId ?? person.staffId ?? undefined,
                  name: person.name, email: person.email,
                  role: person.roleKey || person.defaultRoleKey,
                  init: person.init, color: person.color, assignments: person.assignments,
                });
                if (!started) {
                  toast({ tone: "danger", title: t("staffRoles.cantActAs", "Can't act as this person"), subtitle: i18nT("staffRoles.cantActAsNoRole", "No role is recorded for this person, so there is nothing to act as yet.") });
                  return;
                }
                logAction("staff", person.memberId ?? person.key, person.name, "started acting as");
                toast({ tone: "info", title: i18nT("staffRoles.nowActingAs", "You're now acting as ") + person.name, subtitle: t("staffRoles.leaveFromBanner", "Leave from the banner at the top of the screen.") });
                nav.go("dashboard");
              }}>
              {t("staffRoles.viewAsThisPerson", "View as this person")}
            </Btn>
            <Btn size="sm" variant="secondary" disabled={!person.memberId || busy !== null}
              onClick={() => void run("invite", () => resendInviteOp(person.memberId as string), t("staffRoles.inviteResent", "Invitation resent"), "invite resent")}>
              {t("staffRoles.resendInvite", "Resend invitation")}
            </Btn>
            <Btn size="sm" variant="secondary" disabled={!canWrite || !person.memberId || busy !== null}
              onClick={() => void run("remove", () => removeMemberOp(person.memberId as string), t("staffRoles.accessRemoved", "Workspace access removed"), "access removed").then(onClose)}>
              {t("staffRoles.removeAccess", "Remove workspace access")}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Screen ──────────────────────────────────────────────────────────────── */

export default function StaffAndRoles() {
  const t = useT();
  const { people, memberOf, locations, loading, error, reload } = usePeople();
  const [tab, setTab] = useState<Tab>("people");
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState<string | null>(null);
  const [open, setOpen] = useState<Person | null>(null);
  const [adding, setAdding] = useState(false);
  const [sel, setSel] = useState<Record<string, true>>({});
  const canWrite = can("team.write") || can("team.read");

  /* The per-location role is a PROCESS role — the same catalogue the Roles tab
     edits ("Personal Banker", "Branch Manager"), NOT the four workspace roles
     from listApiRoles(). Operator, 2026-08-08: "the full capabilities of the
     role setup applied on the people tab". Prefixed by process so two roles
     sharing a name across processes stay distinguishable. */
  /* The roles an admin can assign are the ones this VERTICAL actually uses —
     derived from the tenant's own personas (seed-world/<tenant>/personas.json),
     which is the same source the sign-in chooser lists. Offering the full
     ROLE_CATALOG across all five processes showed Aperture admins Store Ops and
     Appointments roles nobody in that tenant holds (operator, 2026-08-08).
     Falls back to the events catalogue only when the roster serves no
     assignments at all, so the picker is never empty. */
  const apiRoles = useMemo(() => {
    const seen = new Map<string, string>();
    for (const person of people) {
      for (const pr of PROCESSES) {
        const roleId = (person.assignments ?? {})[pr.id];
        if (!roleId || seen.has(roleId)) continue;
        const d = getRoleDef(roleId, pr.id);
        seen.set(roleId, pr.label + " · " + (d?.label ?? roleId));
      }
    }
    if (!seen.size) {
      for (const r of listRoles("events" as ProcessId)) seen.set(r.id, r.label);
    }
    /* Roles already recorded AGAINST A LOCATION count too — otherwise a person
       whose only role is a per-location one would open to a picker that cannot
       show what they currently hold. */
    for (const person of people) {
      for (const lr of person.locationRoles) {
        if (!lr.roleKey || seen.has(lr.roleKey)) continue;
        let label = lr.roleKey;
        for (const pr of PROCESSES) {
          const d = getRoleDef(lr.roleKey, pr.id);
          if (d) { label = pr.label + " · " + d.label; break; }
        }
        seen.set(lr.roleKey, label);
      }
      const dk = person.defaultRoleKey;
      if (dk && !seen.has(dk)) {
        let label = dk;
        for (const pr of PROCESSES) {
          const d = getRoleDef(dk, pr.id);
          if (d) { label = pr.label + " · " + d.label; break; }
        }
        seen.set(dk, label);
      }
    }
    return [...seen.entries()].map(([key, label]) => ({ key, label }));
  }, [people]);
  const roleLabelOf = (k: string) => apiRoles.find((r) => r.key === k)?.label ?? k;
  /* Capability count per person — the column Settings › Team & roles carried,
     resolved the same way: process assignments first, events as the fallback. */
  const capCountOf = (p: Person) => {
    const a = p.assignments ?? {};
    const primary = a.events ?? Object.values(a).find(Boolean) ?? p.defaultRoleKey;
    if (!primary && !Object.keys(a).length) return "—";
    return effectivePerms({ role: primary, assignments: a }).size || "—";
  };
  /* LOBs come from the tenant's services catalogue. It is not served to this
     screen yet, so the field renders DISABLED naming the op it waits on —
     omitting it silently would hide the gap. */
  const lobs: string[] = [];

  const rows = people.filter((p) => {
    if (q.trim() && !`${p.name} ${p.email} ${p.title}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (loc && !p.locations.some((l) => l.name === loc)) return false;
    return true;
  });
  const selected = rows.filter((p) => sel[p.key]);

  const statusPill = (p: Person) => {
    if (p.status === "invited") return <Pill tone="warn">{t("staffRoles.invited1", "Invited")}</Pill>;
    if (p.status === "on-leave") return <Pill tone="neutral">{t("staffRoles.onLeave", "On leave")}</Pill>;
    if (p.status === "inactive") return <Pill tone="neutral" dot>{t("staffRoles.inactive", "Inactive")}</Pill>;
    return <Pill tone="success" dot>{t("staffRoles.active", "Active")}</Pill>;
  };

  /* An affordance that cannot do its job renders DISABLED with the reason on
     hover — never an enabled control that fires an apologetic toast
     (CLAUDE.md, no-fake-interactivity). */
  const bulk = (label: string, fn: () => void) => (
    <Btn key={label} size="sm" variant="secondary" onClick={fn}>{label}</Btn>
  );
  const bulkPending = (label: string, op: string) => (
    <Btn key={label} size="sm" variant="secondary" disabled
      title={i18nT("staffRoles.pendingOp", "Not served yet — needs ") + op}>{label}</Btn>
  );

  return (
    <Screen rail="people">
      <PageHeader
        title={t("staffRoles.staffRoles", "Staff & Roles")}
        subtitle={t("staffRoles.subtitle", "Everyone in the workspace — where they work, what they do there, and what they can access.")}
        actions={canWrite && tab === "people" ? <Btn icon={<Icon.plus />} onClick={() => setAdding(true)}>{t("staffRoles.addStaff", "Add staff")}</Btn> : null}
      />

      <div style={{ display: "flex", gap: 2, padding: "0 24px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        {([["people", t("staffRoles.people", "People")], ["groups", t("staffRoles.groups", "Groups")], ["roles", t("staffRoles.roles", "Roles")]] as [Tab, string][]).map(([id, lbl]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding: "0 14px", height: 40, border: "none", background: "transparent", cursor: "pointer", fontFamily: T.font, fontSize: 14, fontWeight: tab === id ? 700 : 500, color: tab === id ? T.p1 : T.soft, boxShadow: tab === id ? `inset 0 -2px 0 ${T.p1}` : "none" }}>
            {lbl}
          </button>
        ))}
      </div>

      {tab === "people" ? (
        <>
          <FilterBar>
            <Input full={false} size="sm" leading={<Icon.search size={14} />} style={{ width: 250 }}
              placeholder={t("staffRoles.searchPeople", "Search name, email or title…")} value={q} onChange={(e) => setQ(e.target.value)} />
            <FilterDropdown label={loc ?? t("staffRoles.anyLocation", "Any location")} active={!!loc}
              options={locations.map((l) => l.name)} onPick={setLoc} onClear={() => setLoc(null)} />
            <span style={{ marginLeft: "auto", fontSize: 12, color: T.soft }}>{rows.length} {t("locations.list.of", "of")} {people.length}</span>
          </FilterBar>

          <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "14px 24px 24px" }}>
            {adding ? <AddStaffForm locations={locations} roles={apiRoles} lobs={lobs} onClose={() => setAdding(false)} onDone={() => { setSel({}); reload(); }} /> : null}

            {selected.length ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", background: T.p4, border: "1px solid #E0D6FB", borderRadius: 9, marginBottom: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.p1 }}>{selected.length} {t("staffRoles.selected", "selected")}</span>
                <span style={{ width: 1, height: 18, background: "#D5C6FA" }} />
                {bulkPending(t("staffRoles.assignLocation", "Assign location"), "bulkAssignLocation")}
                {bulkPending(t("staffRoles.assignLob", "Assign LOB"), "bulkAssignLob")}
                {bulkPending(t("staffRoles.changeRole", "Change role"), "bulkSetRole")}
                {bulk(t("staffRoles.reinvite", "Invite / re-invite"), () => {
                  void Promise.all(selected.filter((p) => p.memberId).map((p) => resendInviteOp(p.memberId as string)))
                    .then(() => { toast({ tone: "success", title: t("staffRoles.invitesSent", "Invitations sent"), subtitle: `${selected.length}` }); setSel({}); })
                    .catch((e) => toast({ tone: "danger", title: t("staffRoles.couldnTInvite", "Couldn't send invitations"), subtitle: e instanceof Error ? e.message : String(e) }));
                })}
                {bulkPending(t("staffRoles.deactivate", "Deactivate"), "bulkDeactivate")}
                {bulkPending(t("staffRoles.addToGroup", "Add to group"), "bulkAddToGroup")}
                {bulkPending(t("common.actions.export", "Export"), "bulkExportPeople")}
                <span style={{ flex: 1 }} />
                <button onClick={() => setSel({})} style={{ border: "none", background: "transparent", color: T.soft, fontSize: 12.5, fontWeight: 500, cursor: "pointer", fontFamily: T.font }}>{t("common.actions.clear", "Clear")}</button>
              </div>
            ) : null}

            <LoadState loading={loading} error={error}>
              {!rows.length ? (
                <EmptyState variant="card" icon={<Icon.users size={20} />} title={t("staffRoles.nobodyMatches", "Nobody matches")} body={t("staffRoles.tryADifferentSearch", "Try a different search or filter.")} />
              ) : (
                <Card style={{ padding: 0, overflow: "hidden" }}>
                  <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: T.n6 }}>
                        <th style={{ ...TH, width: 40 }}>
                          <Checkbox checked={selected.length > 0 && selected.length === rows.length}
                            onChange={() => setSel(selected.length === rows.length ? {} : Object.fromEntries(rows.map((p) => [p.key, true as const])))} />
                        </th>
                        <th style={TH}>{t("staffRoles.person", "Person")}</th>
                        <th style={{ ...TH, width: 190 }}>{t("staffRoles.roleAccess", "Role / access")}</th>
                        <th style={{ ...TH, width: 190 }}>{t("staffRoles.location", "Location")}</th>
                        <th style={{ ...TH, width: 110 }}>{t("staffRoles.status", "Status")}</th>
                        <th style={{ ...TH, width: 84 }}>{t("staffRoles.caps", "Caps")}</th>
                        <th style={{ ...TH, width: 120 }}>{t("staffRoles.lastLogin", "Last login")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((p, i) => (
                        <tr key={p.key} onClick={() => setOpen(p)} style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer", background: sel[p.key] ? "#FBFAFF" : undefined }}>
                          <td style={TD} onClick={(e) => e.stopPropagation()}>
                            <Checkbox checked={!!sel[p.key]} onChange={() => setSel((s) => { const n = { ...s }; if (n[p.key]) delete n[p.key]; else n[p.key] = true; return n; })} />
                          </td>
                          <td style={TD}>
                            <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
                              <Avatar size={30} initials={p.init} color={p.color} />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                                <div style={{ fontSize: 11.5, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.title || p.email}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ ...TD, overflow: "hidden" }}>
                            <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: T.n1 }}>{p.roleLabel || roleLabelOf(p.defaultRoleKey) || "—"}</div>
                            <div style={{ fontSize: 11.5, color: T.soft, display: "flex", gap: 6 }}>
                              {p.signsIn ? <span>{t("staffRoles.signsIn", "Signs in")}</span> : null}
                              {p.bookable ? <span>{t("staffRoles.bookable", "Bookable")}</span> : null}
                              {!p.signsIn && !p.bookable ? <span>{t("staffRoles.recordOnly", "Record only")}</span> : null}
                            </div>
                          </td>
                          <td style={{ ...TD, overflow: "hidden" }}>
                            <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: T.n2 }}>
                              {p.locations.length ? p.locations.map((l) => l.name).join(", ") : "—"}
                            </div>
                          </td>
                          <td style={TD}>{statusPill(p)}</td>
                          <td style={{ ...TD, color: T.n2, fontSize: 12 }}>{capCountOf(p)}</td>
                          <td style={{ ...TD, color: T.soft, fontSize: 12 }}>
                            {p.memberId ? (memberOf(p)?.last ?? "—") : t("staffRoles.neverSignedIn", "No login")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              )}
            </LoadState>
          </div>
        </>
      ) : tab === "groups" ? (
        <div style={{ flex: 1, overflow: "auto", padding: "18px 24px 24px" }}>
          <EmptyState variant="card" icon={<Icon.grid size={20} />}
            title={t("staffRoles.groupsComingHere", "Staff groups move here next")}
            body={t("staffRoles.groupsStillLive", "Group management still lives on the Staff screen this pass — nothing was retired. It moves into this tab once the merged roster is signed off.")} />
        </div>
      ) : (
        <div style={{ flex: 1, overflow: "auto", padding: "18px 24px 24px" }}>
          <RolesTab people={people} />
        </div>
      )}

      {open ? <PersonRecord person={people.find((p) => p.key === open.key) ?? open} locations={locations} roles={apiRoles}
        lastLogin={open.memberId ? (memberOf(open)?.last ?? "—") : t("staffRoles.neverSignedIn", "No login")}
        statusPill={statusPill}
        onClose={() => setOpen(null)} onChanged={() => { setSel({}); reload(); }} /> : null}
    </Screen>
  );
}
